import React, { useRef } from "react";
import { X, Video, CheckCircle2, Sparkles, MapPin, Camera, Clock } from "lucide-react";
import iphoneGuideVideo from "../../assets/instructiuon_video/iphone_attendance_guide.mp4";

interface IPhoneInstructionVideoModalProps {
  open: boolean;
  onClose: () => void;
}

const IPhoneInstructionVideoModal: React.FC<IPhoneInstructionVideoModalProps> = ({
  open,
  onClose,
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/80 backdrop-blur-sm animate-fadeIn font-sans">
      <div className="relative w-full max-w-md bg-white rounded-3xl overflow-hidden shadow-2xl border border-gray-100 flex flex-col max-h-[92dvh]">
        {/* Header Bar */}
        <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white px-4 py-3 flex items-center justify-between shrink-0">
          <div className="flex items-center space-x-2 min-w-0">
            <div className="w-8 h-8 rounded-full bg-[#FF6B1A]/20 flex items-center justify-center text-[#FF6B1A] shrink-0">
              <Video className="w-4 h-4" />
            </div>
            <div className="min-w-0">
              <h2 className="text-xs font-bold text-white truncate">
                iOS Attendance Guide
              </h2>
              <p className="text-[10px] text-gray-300 truncate">
                আইফোন উপস্থিতি নির্দেশিকা
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
            title="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Subtitle / Tip Banner */}
        <div className="bg-orange-50 px-4 py-2 border-b border-orange-100 flex items-center space-x-2 shrink-0">
          <Sparkles className="w-3.5 h-3.5 text-[#FF6B1A] shrink-0" />
          <p className="text-[11px] text-gray-700 font-medium truncate">
            Follow steps below & tap Play to watch video guide
          </p>
        </div>

        {/* Scrollable Content Container (Instructions + Video Player) */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3.5">
          {/* Written Step-by-Step Instructions Card */}


          {/* Video Player Container (NO AUTOPLAY) */}
          <div className="relative w-full bg-black rounded-2xl overflow-hidden shadow-sm border border-gray-800 min-h-[200px] flex items-center justify-center">
            <video
              ref={videoRef}
              src={iphoneGuideVideo}
              className="w-full h-full object-contain max-h-[45vh]"
              controls
              playsInline
              preload="metadata"
            />
          </div>
          <div className="bg-gray-50 mt-3 rounded-2xl p-3.5 border border-gray-100 space-y-2.5 text-xs text-gray-700">
            <h3 className="font-bold text-gray-900 text-xs flex items-center space-x-1.5 border-b border-gray-200 pb-2">
              <CheckCircle2 className="w-4 h-4 text-[#FF6B1A]" />
              <span>Step-by-Step Instructions (উপস্থিতি নির্দেশাবলী)</span>
            </h3>

            <div className="flex items-start space-x-2.5">
              <div className="w-6 h-6 rounded-lg bg-orange-100 text-[#FF6B1A] flex items-center justify-center font-bold text-[11px] shrink-0 mt-0.5">
                <MapPin className="w-3.5 h-3.5" />
              </div>
              <p className="flex-1 leading-snug">
                <strong>1. Location Services:</strong> Open iPhone <em>Settings &rarr; Privacy &amp; Security &rarr; Location Services &rarr; Safari Websites</em> &rarr; set to <strong>While Using the App</strong>.
              </p>
            </div>

            <div className="flex items-start space-x-2.5">
              <div className="w-6 h-6 rounded-lg bg-orange-100 text-[#FF6B1A] flex items-center justify-center font-bold text-[11px] shrink-0 mt-0.5">
                <Camera className="w-3.5 h-3.5" />
              </div>
              <p className="flex-1 leading-snug">
                <strong>2. Safari Permissions:</strong> In iPhone <em>Settings &rarr; Safari &rarr; Camera &amp; Location</em> &rarr; set both to <strong>Allow</strong>.
              </p>
            </div>

            <div className="flex items-start space-x-2.5">
              <div className="w-6 h-6 rounded-lg bg-orange-100 text-[#FF6B1A] flex items-center justify-center font-bold text-[11px] shrink-0 mt-0.5">
                <Clock className="w-3.5 h-3.5" />
              </div>
              <p className="flex-1 leading-snug">
                <strong>3. Mark Attendance:</strong> Tap <strong>Clock In Now</strong>, align your face inside the frame, and wait for automatic facial verification.
              </p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-3.5 bg-white border-t border-gray-100 flex items-center justify-between space-x-3 shrink-0">
          <div className="flex items-center space-x-1.5 text-emerald-600 text-xs font-semibold">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            <span>Ready for attendance</span>
          </div>

          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-2xl bg-gradient-to-r from-[#FF6B1A] to-[#EA580C] text-white text-xs font-bold shadow-md hover:shadow-lg active:scale-95 transition-all"
          >
            Got It / বুঝেছি
          </button>
        </div>
      </div>
    </div>
  );
};

export default IPhoneInstructionVideoModal;

